// La caja de escribir a Santi (propuesta §5.1, P1/P4): lo único que firma es «Enviar». En un contrato que te toca,
// enviar le pasa el turno a Santi (la oficina deriva lo que le toca); la pista bajo la caja lo dice antes de enviar y deja
// el caso raro a un clic: «Al enviar, le toca a Santi · No, sigo yo» ↔ «Al enviar, te sigue tocando a ti · Que le toque a
// Santi» (vuelve a lo de siempre tras cada envío). Si espera a Santi, o en el chat, no hay pista: escribir no cambia nada.
// Enter envía, Mayús+Enter salta de línea; el borrador se guarda por hilo en el navegador (borradores.ts). Un envío que
// falló sin saber si llegó (5xx, red) se reintenta con la MISMA clave: el centro no lo repite; un «no» del servidor
// (4xx) termina aquel envío y el siguiente es otro mensaje con otra clave.
// Entrega 3 (04-11 noche, Pau: «no quiero que el agente trabaje solo, siempre con mi ayuda»): NADA entra solo en esta
// caja. Lo que propone el agente de este hilo sale en SU chat (la columna de al lado) como «Propuesta para Santi»; solo
// «Usar en la caja» lo trae aquí, editable, con «Deshacer» 8 s (store `paraLaCaja`). «Que lo prepare tu agente» le pide
// una propuesta (sale en su chat). Enviar lo manda como mensaje de Pau, igual que cualquier otro: es Pau quien firma.
// (Fuera el borrador automático de la entrega 2 y su «Descartar» / «Otra versión».)
// 04-11 (ADR D-037): la caja de la oficina (Compositor) — un campo con filo sobre el gris de ventana, sin barra de borde a
// borde, esquinas de 22; «Enviar» en cápsula de oro (el único oro), lo demás en cápsulas fantasma en gris.
import { Bot, Loader2, SendHorizontal } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from '../../../i18n';
import { ApiHttpError } from '../../../lib/api';
import { shouldSendOnEnter } from '../../../lib/composer-keys';
import { cn } from '../../../lib/utils';
import { MD_QUERY, useMediaQuery } from '../../../hooks/useMediaQuery';
import { guardarBorrador, useBorrador } from '../chat/borradores';
import { envioTermino, mensajeErrorPelotas } from '../errores';
import { Boton } from '../ui';
import { LIMITES, pelotasApi } from './api';
import { Contador, ENLACE } from './piezas';
import { DURACION_LINEA_MS, pedirHilo, usePelotasStore } from './store';
import type { PropuestaDTO } from './tipos';

export const claveBorradorSanti = (hilo: number | 'general') => `santi:${hilo}`;

/** Si el agente no deja nada en este tiempo tras «Que lo prepare tu agente», el botón vuelve y se dice. */
export const ESPERA_PREPARAR_MS = 3 * 60_000;

/** El mensaje de un fallo: si no se sabe si llegó, el de la propuesta; si el servidor dijo que no, su motivo. */
export function errorDeEnvio(e: unknown): string {
  return envioTermino(e) ? mensajeErrorPelotas(e) : t('oficinaV2.pelotas.chat.noSePudo');
}

/** El servidor no aceptó los datos (400 DATOS_INVALIDOS): se dice qué, en palabras, si se sabe. */
export const datosInvalidos = (e: unknown): boolean => e instanceof ApiHttpError && e.code === 'DATOS_INVALIDOS';

const mensajeLargo = (texto: string): string | null => (texto.trim().length > LIMITES.texto ? t('oficinaV2.pelotas.chat.textoLargo', { max: LIMITES.texto }) : null);

/** «Que lo prepare tu agente»: pide y espera a que llegue una propuesta nueva a su chat (o se cansa y lo dice). */
export function usePreparar(hilo: number | 'general', enActual: string | null) {
  const preparar = usePelotasStore((s) => s.preparar);
  const [preparando, setPreparando] = useState<{ antes: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (preparando && enActual && enActual !== preparando.antes) setPreparando(null);
  }, [preparando, enActual]);
  useEffect(() => {
    if (!preparando) return;
    const id = setTimeout(() => {
      setPreparando(null);
      setError(t('oficinaV2.pelotas.borrador.sinRespuesta'));
    }, ESPERA_PREPARAR_MS);
    return () => clearTimeout(id);
  }, [preparando]);
  const pedir = useCallback(async () => {
    if (preparando) return;
    setError(null);
    setPreparando({ antes: enActual });
    try {
      await preparar(hilo);
    } catch {
      setPreparando(null);
      setError(t('oficinaV2.pelotas.borrador.noSePudo'));
    }
  }, [preparando, enActual, preparar, hilo]);
  return { preparando: !!preparando, error, pedir, limpiarError: () => setError(null) };
}

export function CompositorSanti({
  hilo,
  conTurno,
  corta = false,
  propuesta = null,
  alPreparar,
}: {
  hilo: number | 'general';
  /** Le toca a Pau en este contrato: enviar le pasa el turno a Santi (y sale la pista). */
  conTurno: boolean;
  /** Tres columnas (el agente al lado): la pista en corto («Le toca a Santi · No, sigo yo»). */
  corta?: boolean;
  /** La propuesta vigente del agente de este hilo (en SU chat): «Preparando…» espera a que cambie. */
  propuesta?: PropuestaDTO | null;
  /** «Que lo prepare tu agente» pulsado (el iPad abre el agente encima, para verlo trabajar). */
  alPreparar?: () => void;
}) {
  const claveCaja = claveBorradorSanti(hilo);
  const [texto, setTexto] = useBorrador(claveCaja);
  const [seguirYo, setSeguirYo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clave = useRef<string | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  const dedo = useMediaQuery('(pointer: coarse)');
  const anchoEscritorio = useMediaQuery(MD_QUERY);
  const enterSaltaLinea = dedo && !anchoEscritorio;
  const prep = usePreparar(hilo, propuesta?.en ?? null);

  // Entrega 3 — lo único que pone texto en esta caja sin teclearlo: «Usar en la caja» en el chat del agente. Se recoge
  // una vez, queda editable y, 8 s, «Deshacer» devuelve lo que había.
  const paraLaCaja = usePelotasStore((s) => s.paraLaCaja);
  const [deshacer, setDeshacer] = useState<{ previo: string } | null>(null);
  const reloj = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!paraLaCaja || paraLaCaja.hilo !== hilo) return;
    usePelotasStore.getState().recogerParaLaCaja();
    setDeshacer({ previo: texto });
    setTexto(paraLaCaja.texto);
    area.current?.focus();
    if (reloj.current) clearTimeout(reloj.current);
    reloj.current = setTimeout(() => setDeshacer(null), DURACION_LINEA_MS);
    // Solo cuando llega una petición nueva (el texto de ahora se lee en ese momento).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paraLaCaja, hilo]);
  useEffect(() => {
    return () => {
      if (reloj.current) clearTimeout(reloj.current);
    };
  }, []);

  useEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [texto]);

  async function enviar() {
    // Sin nada que mandar, Enviar solo devuelve el foco a la caja (nunca un error).
    if (!texto.trim()) {
      area.current?.focus();
      return;
    }
    if (enviando) return;
    // Un texto de antes puede pasar del límite (la caja ya no deja escribir más): se dice antes de enviar.
    const largo = mensajeLargo(texto);
    if (largo) {
      setError(largo);
      area.current?.focus();
      return;
    }
    setEnviando(true);
    setError(null);
    clave.current ??= crypto.randomUUID();
    try {
      await pelotasApi.mensaje(hilo, { texto: texto.trim(), seguirYo: conTurno && seguirYo }, clave.current);
      clave.current = null;
      setDeshacer(null);
      setTexto('');
      guardarBorrador(claveCaja, '');
      setSeguirYo(false);
      pedirHilo(hilo);
      void usePelotasStore.getState().cargarLista();
      area.current?.focus();
    } catch (e) {
      if (envioTermino(e)) clave.current = null;
      setError((datosInvalidos(e) && mensajeLargo(texto)) || errorDeEnvio(e));
    } finally {
      setEnviando(false);
    }
  }

  const avisoError = error ?? prep.error;
  return (
    <footer className="shrink-0 bg-oficina-elevada px-6 pb-4 pt-2" data-testid="compositor-santi">
      {avisoError ? (
        <p role="alert" className="mb-2 px-1 text-o-sub text-oficina-error">
          {avisoError}
        </p>
      ) : null}
      {deshacer ? (
        <p className="mb-2 flex items-center gap-1.5 px-1 text-o-sub text-oficina-texto2" data-testid="borrador-puesto">
          {t('oficinaV2.pelotas.borrador.puesto')} ·
          <button
            type="button"
            onClick={() => {
              setTexto(deshacer.previo);
              setDeshacer(null);
              area.current?.focus();
            }}
            className={ENLACE}
            data-testid="borrador-deshacer"
          >
            {t('oficinaV2.pelotas.borrador.deshacer')}
          </button>
        </p>
      ) : null}
      <div className={cn(CAJA, CAJA_NORMAL)} data-testid="compositor-caja">
        <textarea
          ref={area}
          rows={1}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (!e.nativeEvent.isComposing && shouldSendOnEnter(e, { isCoarsePointer: enterSaltaLinea })) {
              e.preventDefault();
              void enviar();
            }
          }}
          maxLength={LIMITES.texto}
          placeholder={t('oficinaV2.pelotas.chat.escribe')}
          aria-label={t('oficinaV2.pelotas.chat.escribe')}
          className="block max-h-[200px] min-h-[52px] w-full resize-none bg-transparent px-4 pb-1 pt-3.5 text-o-cuerpo text-oficina-texto outline-none placeholder:text-oficina-texto3"
          data-testid="compositor-texto"
        />
        <div className="flex items-center gap-2 px-1.5 pb-1.5 pt-1">
          <BotonAgente
            icono={Bot}
            texto={t('oficinaV2.pelotas.borrador.preparar')}
            preparando={prep.preparando}
            alPulsar={() => {
              alPreparar?.();
              void prep.pedir();
            }}
          />
          <span className="flex-1 pl-1">
            <Contador n={texto.length} max={LIMITES.texto} />
          </span>
          {conTurno ? (
            <span className="mr-1 inline-flex min-w-0 items-center gap-1.5 whitespace-nowrap text-o-sub text-oficina-texto3" data-testid="compositor-pista">
              {t(seguirYo ? (corta ? 'oficinaV2.pelotas.chat.pistaSiguesCorta' : 'oficinaV2.pelotas.chat.pistaSigues') : corta ? 'oficinaV2.pelotas.chat.pistaLeTocaCorta' : 'oficinaV2.pelotas.chat.pistaLeToca')} ·
              <button type="button" onClick={() => setSeguirYo((v) => !v)} aria-pressed={seguirYo} className={ENLACE} data-testid="compositor-seguir-yo">
                {t(seguirYo ? 'oficinaV2.pelotas.chat.queLeToque' : 'oficinaV2.pelotas.chat.noSigoYo')}
              </button>
            </span>
          ) : null}
          <Boton variante="primario" aria-disabled={!texto.trim()} onClick={() => void enviar()} data-testid="compositor-enviar">
            {enviando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <SendHorizontal className="h-4 w-4" strokeWidth={2.2} aria-hidden />}
            {t('oficinaV2.pelotas.chat.enviar')}
          </Boton>
        </div>
      </div>
    </footer>
  );
}

/** La caja de escribir (también la de «Pedir algo»): el campo con filo de la oficina (chat/Compositor). */
export const CAJA = 'rounded-o-xl border transition-colors duration-150 ease-o-apple';
export const CAJA_NORMAL = 'border-white/[0.12] bg-oficina-fondo/40 focus-within:border-white/20';
/** Con el pedido que preparó un agente («Pedir algo» tras «Revisar»): el tono de «lo tuyo, aún no lo ha visto Santi». */
export const CAJA_BORRADOR = 'border-oficina-acento/[0.22] bg-oficina-tuyo';

/** Un botón fantasma en gris (el oro de `fantasma` es para enlaces sueltos; aquí el oro es solo «Enviar»). */
export const FANTASMA_GRIS = 'text-oficina-texto2 hover:text-oficina-texto';

/**
 * La fila de arriba de la caja de «Pedir algo» con el pedido que preparó un agente (tras «Revisar» en su chat): «Borrador
 * de tu agente · <porqué>» y «Descartar».
 */
export function CabeceraBorrador({ porque, alDescartar }: { porque: string; alDescartar: () => void }) {
  return (
    <div className="flex h-11 items-center gap-2 pl-4 pr-1.5 pt-1 text-o-sub font-semibold text-oficina-meta-pau" data-testid="borrador-cabecera">
      <Bot className="h-4 w-4 shrink-0" aria-hidden />
      <span className="shrink-0">{t('oficinaV2.pelotas.borrador.etiqueta')}</span>
      {porque ? (
        <span className="min-w-0 truncate font-normal text-oficina-texto2" data-testid="borrador-porque">
          · {porque}
        </span>
      ) : null}
      <span className="flex-1" />
      <Boton variante="fantasma" tamano="sm" onClick={alDescartar} className={FANTASMA_GRIS} data-testid="borrador-descartar">
        {t('oficinaV2.pelotas.borrador.descartar')}
      </Boton>
    </div>
  );
}

/** «Que lo prepare tu agente» (fantasma en gris, con el robot); mientras espera su propuesta, «Preparando…» (sin
 *  apagarse: es lo que dice que está trabajando). */
export function BotonAgente({ icono: Icono, texto, preparando, alPulsar }: { icono: typeof Bot; texto: string; preparando: boolean; alPulsar: () => void }) {
  return (
    <Boton
      variante="fantasma"
      onClick={alPulsar}
      disabled={preparando}
      aria-busy={preparando || undefined}
      className={cn(FANTASMA_GRIS, 'px-3.5 disabled:opacity-100')}
      data-testid="borrador-preparar"
    >
      {preparando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Icono className="h-4 w-4" aria-hidden />}
      {preparando ? t('oficinaV2.pelotas.borrador.preparando') : texto}
    </Boton>
  );
}
